import React, { useState, useEffect } from 'react';
import './SnapshotsDialog.css';

interface Snapshot {
  id: number;
  backup_type: 'manual' | 'automatic' | 'daily';
  description: string;
  created_at: string;
  created_by_name?: string;
  snapshot_data: any;
}

interface SnapshotsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  onRestore: (snapshotId: number) => void;
}

const SnapshotsDialog: React.FC<SnapshotsDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  onRestore,
}) => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'manual' | 'automatic' | 'daily'>('all');

  useEffect(() => {
    if (isOpen) {
      fetchSnapshots();
    }
  }, [isOpen, projectId]);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/snapshots/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSnapshots(data);
      } else {
        console.error('Failed to fetch snapshots:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching snapshots:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredSnapshots = filter === 'all' 
    ? snapshots 
    : snapshots.filter(s => s.backup_type === filter);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'manual': return '👤';
      case 'automatic': return '🤖';
      case 'daily': return '📅';
      default: return '📸';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'manual': return 'Manuálna';
      case 'automatic': return 'Automatická';
      case 'daily': return 'Denná';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('sk-SK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRestore = (snapshot: Snapshot) => {
    const confirmed = window.confirm(
      `Naozaj chcete obnoviť projekt do stavu zo zálohy?\n\n` +
      `Záloha: ${snapshot.description}\n` +
      `Vytvorená: ${formatDate(snapshot.created_at)}\n\n` +
      `UPOZORNENIE: Všetky aktuálne zmeny budú prepísané!`
    );

    if (confirmed) {
      onRestore(snapshot.id);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content snapshots-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📦 Zálohy projektu: {projectName}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="snapshots-filters">
            <button 
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              Všetky ({snapshots.length})
            </button>
            <button 
              className={filter === 'manual' ? 'active' : ''}
              onClick={() => setFilter('manual')}
            >
              👤 Manuálne ({snapshots.filter(s => s.backup_type === 'manual').length})
            </button>
            <button 
              className={filter === 'automatic' ? 'active' : ''}
              onClick={() => setFilter('automatic')}
            >
              🤖 Automatické ({snapshots.filter(s => s.backup_type === 'automatic').length})
            </button>
            <button 
              className={filter === 'daily' ? 'active' : ''}
              onClick={() => setFilter('daily')}
            >
              📅 Denné ({snapshots.filter(s => s.backup_type === 'daily').length})
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Načítavam zálohy...</p>
            </div>
          ) : filteredSnapshots.length === 0 ? (
            <div className="empty-state">
              <p>📭 Žiadne zálohy</p>
              <p className="empty-hint">
                {filter === 'all' 
                  ? 'Zatiaľ neboli vytvorené žiadne zálohy projektu.'
                  : `Žiadne ${getTypeLabel(filter).toLowerCase()} zálohy.`}
              </p>
            </div>
          ) : (
            <div className="snapshots-list">
              {filteredSnapshots.map((snapshot) => (
                <div key={snapshot.id} className="snapshot-item">
                  <div className="snapshot-icon">
                    {getTypeIcon(snapshot.backup_type)}
                  </div>
                  <div className="snapshot-info">
                    <div className="snapshot-description">{snapshot.description}</div>
                    <div className="snapshot-meta">
                      <span className="snapshot-type">{getTypeLabel(snapshot.backup_type)}</span>
                      <span className="snapshot-date">{formatDate(snapshot.created_at)}</span>
                      {snapshot.created_by_name && (
                        <span className="snapshot-author">👤 {snapshot.created_by_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="snapshot-actions">
                    <button 
                      className="btn-restore"
                      onClick={() => handleRestore(snapshot)}
                      title="Obnoviť projekt do tohto stavu"
                    >
                      ↶ Obnoviť
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            <p>💡 Automatické zálohy sa vytvárajú pred každým vymazaním.</p>
            <p>📅 Denné zálohy sa vytvárajú každý deň o 2:00.</p>
          </div>
          <button className="btn-close" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnapshotsDialog;

